<template>
  <c-container
    padding-size="small"
    :style="{ overflow: 'auto', height: 'calc(100vh - 4.8rem - 4rem - 7.45rem)', background: 'white' }"
  >
    <upgrade-hint v-if="usesProFeatures" />
    <c-list>
      <c-input
        v-model:value="name"
        type="text"
        show-outline
        show-background
        show-border
        placeholder="Name your template..."
        autofocus
      />
      <div
        v-for="(property, idx) in properties"
        :key="idx"
      >
        <property-view
          :property-value="property"
          @update:value="change($event, idx)"
        />
      </div>
    </c-list>
    <c-divider />
    <div
      v-if="!canUseBlocks && blocks.length === 0"
      :style="{ display: 'flex', alignItems: 'center', fontSize: $fontSizes[5] }"
    >
      <img
        src="/illustrations/cubes.svg"
        :style="{ width: '20rem', height: 'auto' }"
      >
      <div>
        <div :style="{ margin: '1rem 0' }">
          Upgrade to customize your page with pre-defined blocks.
        </div>
        <c-button
          outlined
          :href="upgradeUrl"
          target="_blank"
        >
          Upgrade plan
        </c-button>
      </div>
    </div>
    <template
      v-else
    >
      <draggable
        :model-value="blocks"
        item-key="uid"
        handle="._handle"
        ghost-class="-ghost"
        @update:model-value="handleDrag"
      >
        <template #item="{element}">
          <block-item
            :key="element.uid"
            :block="element"
            :style="{ marginBottom: '1rem' }"
            class="_block"
            @update:value="updateBlock(element, $event)"
            @delete="removeBlock($event)"
          />
        </template>
      </draggable>
      <c-button
        icon="plus"
        :style="{ marginBottom: '8rem' }"
        color="light"
        @click="addBlock"
      >
        Add block
      </c-button>
    </template>
  </c-container>
  <c-container
    padding-size="small"
    :style="{ borderTop: `1px solid ${$colors.grey.lighten3}`, background: 'white' }"
  >
    <c-button
      color="primary"
      stretch
      @click="saveTemplate"
    >
      Save
    </c-button>
  </c-container>
</template>

<script>
import BlockItem from '@/components/BlockItem.vue';
import CButton from 'shared/components/CButton.vue';
import CContainer from 'shared/components/CContainer.vue';
import CDivider from 'shared/components/CDivider.vue';
import CInput from 'shared/components/CInput.vue';
import CList from 'shared/components/CList.vue';
import Draggable from 'vuedraggable';
import PropertyView from '@/components/PropertyView.vue';
import UpgradeHint from '@/components/UpgradeHint.vue';
import useFeatureAccess from 'shared/services/feature-access';
import useSubscription from 'shared/services/subscription';
import {
  BETA_PHASE_MANAGER,
  TEMPLATE_SERVICE,
  USER_SERVICE, featureFlags,
} from 'shared/constants';
import { inject } from 'vue';
import { uuidv4 } from 'shared/services/firebase';

export default {
  name: 'EditTemplate',
  components: { UpgradeHint, Draggable, CDivider, BlockItem, PropertyView, CContainer, CInput, CButton, CList },
  emits: ['leave'],
  setup() {
    const templateService = inject(TEMPLATE_SERVICE);
    const userService = inject(USER_SERVICE);
    const betaPhaseManager = inject(BETA_PHASE_MANAGER);
    const { currentPlan, subscriptionPastDue } = useSubscription(userService.currentUser, betaPhaseManager);
    const { hasFeatureAccess } = useFeatureAccess(currentPlan, subscriptionPastDue, betaPhaseManager);
    return {
      template: templateService.selectedTemplate,
      templateService,
      hasFeatureAccess,
    };
  },
  data() {
    return {
      name: '',
      properties: {},
      blocks: [],
    };
  },
  computed: {
    upgradeUrl() {
      return `${import.meta.env.VITE_WEBSITE_URL}/app/plans`;
    },
    usesProFeatures() {
      return this.blocks.length > 0 || this.hasAIProperties;
    },
    hasAIProperties() {
      const properties = Object.values(this.properties);
      for (let i = 0; i < properties.length; i++) {
        if (properties[i].prefill === 'ai') {
          return true;
        }
      }
      return false;
    },
    canUseBlocks() {
      return this.hasFeatureAccess(featureFlags.pageBlocks);
    },
  },
  methods: {
    handleDrag(val) {
      this.blocks = val;
    },
    updateBlock(block, val) {
      const index = this.blocks.findIndex((b) => b.uid === block.uid);
      this.blocks[index] = val;
    },
    removeBlock(block) {
      const index = this.blocks.findIndex((b) => b.uid === block.uid);
      this.blocks.splice(index, 1);
    },
    addBlock() {
      this.blocks.push({ type: 'text', uid: uuidv4() });
    },
    async initiateTemplate() {
      this.name = this.template.name;
      const properties = JSON.parse(JSON.stringify(this.template.properties));
      const titleProperty = properties.title;
      delete properties.title;
      this.properties = { title: titleProperty, ...properties };
      if (this.template.blocks !== undefined) {
        this.blocks = this.template.blocks;
      }
    },
    change(property, id, isEndDate = false) {
      if (isEndDate) {
        this.properties[id].endDate = property;
      } else {
        this.properties[id] = property;
      }
    },
    toggleEndDate(id) {
      this.properties[id].endTime = !this.properties[id].endTime;
    },
    saveTemplate() {
      const dataObj = {
        database: this.template.database,
        name: this.name,
        properties: this.properties,
        blocks: this.blocks,
      };
      this.templateService.updateSingle(this.template.id, dataObj);
      this.$emit('leave');
    },
  },
  created() {
    this.initiateTemplate();
  },
};
</script>

<style scoped lang="scss" type="text/scss">
._block {
  &.-ghost {
    opacity: .4;
  }
}
</style>
